import { useState, type FormEvent, type JSX } from 'react';
import { authLogin } from '@peacock/sdk-js';
import { useAuthStore } from './authStore';

/**
 * Phase-3 real user login. Replaces the developer-facing
 * TokenPasteShell as the default entry point.
 *
 * The TokenPasteShell stays available for power users (and during
 * development) via the small "Have a token? Paste it" link in the
 * footer — they get pushed into a separate component when clicked.
 */
export function LoginForm({
  onSwitchToSignup,
  onSwitchToTokenPaste,
}: {
  onSwitchToSignup: () => void;
  onSwitchToTokenPaste: () => void;
}): JSX.Element {
  const setSession = useAuthStore((s) => s.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setStatus({ kind: 'busy' });

    const baseUrl =
      (import.meta.env.VITE_PEACOCK_API_BASE_URL as string | undefined) ??
      window.location.origin;

    const result = await authLogin(baseUrl, {
      email: email.trim().toLowerCase(),
      password,
    });

    if (result.status === 'ok') {
      setSession(
        result.data.data.token,
        result.data.data.user,
        // `spaces` is optional in the type for backwards-compat with
        // older API versions; live Peacock APIs always send it.
        result.data.data.spaces ?? [],
      );
      return;
    }
    if (result.status === 'error' && result.code === 401) {
      setStatus({ kind: 'error', message: 'E-Mail oder Passwort falsch.' });
      return;
    }
    if (result.status === 'error' && result.code === 422) {
      setStatus({ kind: 'error', message: 'Bitte E-Mail und Passwort prüfen.' });
      return;
    }
    if (result.status === 'error' && result.code === 429) {
      setStatus({ kind: 'error', message: 'Zu viele Versuche — kurz warten.' });
      return;
    }
    setStatus({
      kind: 'error',
      message:
        result.status === 'error' ? `HTTP ${result.code}` : 'Login fehlgeschlagen',
    });
  }

  return (
    <main className="grid min-h-screen place-items-center bg-zinc-50 px-4">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <header className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
            Anmelden
          </h1>
          <p className="mt-1 text-sm text-zinc-600">
            E-Mail-Adresse und Passwort.
          </p>
        </header>

        <div className="flex flex-col gap-3">
          <div>
            <label
              htmlFor="login-email"
              className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500"
            >
              E-Mail
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              className="min-h-11 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500"
            >
              Passwort
            </label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              data-testid="login-password"
              className="min-h-11 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
            />
          </div>
        </div>

        {status.kind === 'error' ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          >
            {status.message}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status.kind === 'busy'}
          data-testid="login-submit"
          className="mt-6 min-h-11 w-full rounded-md bg-zinc-900 px-4 py-2 font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:bg-zinc-300"
        >
          {status.kind === 'busy' ? 'Anmelden…' : 'Anmelden'}
        </button>

        <div className="mt-6 flex flex-col gap-2 text-xs text-zinc-500">
          {/* Wrap each secondary action in an inline-flex 44-tall row so
              the tap target meets HIG without inflating the visible text
              (responsive-audit-fixer 2026-05-30 MEDIUM). */}
          <p className="inline-flex min-h-11 items-center">
            <span>Noch kein Konto?&nbsp;</span>
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="inline-flex min-h-11 items-center px-1 text-zinc-900 underline hover:text-zinc-700"
            >
              Jetzt registrieren
            </button>
          </p>
          <p className="inline-flex min-h-11 items-center text-[11px] text-zinc-400">
            <span>Entwickler:innen mit API-Token →&nbsp;</span>
            <button
              type="button"
              onClick={onSwitchToTokenPaste}
              className="inline-flex min-h-11 items-center px-1 underline hover:text-zinc-600"
            >
              Token-Login
            </button>
          </p>
        </div>
      </form>
    </main>
  );
}
